import { useMutation } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import React, { useState } from "react";

import insertCompany, {
  InsertCompanyResult,
  InsertCompanyVariables,
} from "../../graphqlQueries/companyInsert";
import { getJoinRequestsForUser } from "../../graphqlQueries/getJoinRequests";
import processJoinRequestQuery, {
  ProcessJoinRequestResult,
  ProcessJoinRequestVariables,
} from "../../graphqlQueries/processJoinRequest";
import {
  CompanyInformation,
  DatahubType,
  JoinRequestStatus,
  LoginCompanyAccount,
  UserRole,
} from "../../types";
import { getHasuraRoleContext } from "../../utils/functions";
import CreateAccountError from "./CreateAccountError";
import CreateAccountIntro from "./CreateAccountIntro";
import CreateAccountSaveConfirmation from "./CreateAccountSaveConfirmation";
import CreateCompanyAccountForm from "./CreateCompanyAccountForm";

export type WizardStep =
  | "CreateAccountIntro"
  | "CreateAccountForm"
  | "SaveConfirmation"
  | "SaveError"
  | "JoinError";

type CreateAccountWizardProps = {
  companyInformation?: CompanyInformation;
  backButtonOnClick?: () => void;
  isCurator: boolean;
};

const CreateCompanyAccountWizard = ({
  companyInformation,
  backButtonOnClick,
  isCurator,
}: CreateAccountWizardProps) => {
  const [step, setStep] = useState<WizardStep>("CreateAccountIntro");
  const [companyAccount, setCompanyAccount] = useState<LoginCompanyAccount>();

  const [insertCompanyInfo] = useMutation<InsertCompanyResult, InsertCompanyVariables>(
    insertCompany
  );

  const [processJoinRequest] = useMutation<ProcessJoinRequestResult, ProcessJoinRequestVariables>(
    processJoinRequestQuery
  );

  const { keycloak } = useKeycloak();

  const saveAccount = (account: LoginCompanyAccount) => {
    if (!companyInformation) {
      console.error("Company information is missing");
      return;
    }
    const baseContext = getHasuraRoleContext(UserRole.CreateCompany);

    const headers = {
      ...baseContext.headers,
      "x-selected-business-id": companyInformation.businessId,
    };

    insertCompanyInfo({
      context: {
        ...baseContext,
        headers,
      },
      refetchQueries: [getJoinRequestsForUser],
      variables: {
        businessName: account.companyName,
        city: account.city,
        datahubType: isCurator ? DatahubType.DMO : DatahubType.TC,
        description: "",
        dmoAreas: isCurator
          ? account.cities?.map((city) => ({
              curationAreaId: city.value,
            })) ?? []
          : [],
        email: account.email,
        phone: account.phone,
        postalCode: account.postalCode,
        streetName: account.streetName,
        websiteUrl: "",
      },
    })
      .then((result) => {
        if (!result.data) {
          throw new Error("No data from InsertCompany");
        }
        const newCompanyId = result.data.InsertCompany.id;
        if (isCurator) {
          setStep("SaveConfirmation");
        } else {
          const joinRequestId = result.data.InsertCompany.joinRequestId;
          processJoinRequest({
            context: getHasuraRoleContext(UserRole.CreateCompany),
            variables: { approveRequest: true, id: joinRequestId },
          })
            .then((res) => {
              if (res.data?.ProcessJoinRequest.joinRequest.status === JoinRequestStatus.Approved) {
                keycloak?.login({
                  redirectUri: `${process.env.REACT_APP_PUBLIC_URL}/company/${newCompanyId}`,
                });
              } else {
                setStep("SaveConfirmation");
              }
            })
            .catch((err) => {
              console.log(err);
              setStep("JoinError");
            });
        }
      })
      .catch((err) => {
        console.log(err);
        setStep("SaveError");
      });
  };

  if (!companyInformation) {
    return null;
  }

  return (
    <div>
      {step === "CreateAccountIntro" && companyInformation && (
        <CreateAccountIntro
          onNext={() => {
            setStep("CreateAccountForm");
          }}
          companyInformation={companyInformation}
          isCurator={isCurator}
          backButtonOnClick={backButtonOnClick}
        />
      )}
      {step === "CreateAccountForm" && (
        <CreateCompanyAccountForm
          onBack={(account) => {
            setCompanyAccount(account);
            setStep("CreateAccountIntro");
          }}
          onSubmit={(account) => {
            setCompanyAccount(account);
            saveAccount(account);
          }}
          companyAccount={companyAccount}
          isCurator={isCurator}
        />
      )}
      {step === "SaveConfirmation" && <CreateAccountSaveConfirmation isCurator={isCurator} />}
      {step === "SaveError" && (
        <CreateAccountError
          headerKey="signup.createAccountErrorHeader"
          descriptionKey="signup.createAccountErrorDesc"
        />
      )}
      {step === "JoinError" && (
        <CreateAccountError
          headerKey="signup.joinCompanyErrorHeader"
          descriptionKey="signup.joinCompanyErrorDesc"
        />
      )}
    </div>
  );
};

export default CreateCompanyAccountWizard;
